import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "react-feather";
import { Badge, Button, Col, Form, Modal, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { toast } from "react-toastify";
import {
  bulkRetryJobsAPI,
  getRetryableJobsAPI,
  scheduleRetryAPI,
} from "../../../Services/mockApi";
import PageHeader from "../../../Components/shared/PageHeader";
import LoadingSkeleton from "../../../Components/shared/LoadingSkeleton";
import EmptyState from "../../../Components/shared/EmptyState";
import DataTableWrapper from "../../../Components/shared/DataTableWrapper";
import ConfirmModal from "../../../Components/shared/ConfirmModal";
import { platformUrl } from "../../../utils/mhNav";
import styles from "./RetryCenter.module.scss";

export default function RetryCenter() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fQueue, setFQueue] = useState("");
  const [fProvider, setFProvider] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fRange, setFRange] = useState("7d");
  const [selected, setSelected] = useState({});
  const [bulkOpen, setBulkOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleAt, setScheduleAt] = useState(new Date(Date.now() + 3600000));

  const [strategy, setStrategy] = useState("immediate");
  const [delayMin, setDelayMin] = useState(5);
  const [backoff, setBackoff] = useState("none");
  const [maxRetries, setMaxRetries] = useState(3);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRetryableJobsAPI({
        queue: fQueue || undefined,
        provider: fProvider || undefined,
        status: fStatus || undefined,
        timeRange: fRange === "24h" ? "24h" : "7d",
      });
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [fQueue, fProvider, fStatus, fRange]);

  useEffect(() => {
    load();
  }, [load]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((k) => selected[k]),
    [selected],
  );
  const selectedRows = useMemo(
    () => rows.filter((r) => selected[r.id]),
    [rows, selected],
  );
  const safeCount = selectedRows.filter((r) => r.idempotent).length;

  const columns = useMemo(
    () => [
      {
        name: "Job ID",
        selector: (r) => r.jobId,
        sortable: true,
      },
      {
        name: "Queue",
        selector: (r) => r.queue,
        sortable: true,
      },
      {
        name: "Tenant",
        selector: (r) => r.tenantCode,
        sortable: true,
      },
      {
        name: "Error",
        grow: 2,
        cell: (r) => (
          <span title={r.errorSnippet}>
            {r.errorSnippet?.length > 48
              ? `${r.errorSnippet.slice(0, 48)}…`
              : r.errorSnippet}
          </span>
        ),
      },
      {
        name: "Failed at",
        selector: (r) => new Date(r.failedAt).toLocaleString(),
        sortable: true,
      },
      {
        name: "Attempts",
        selector: (r) => r.attempts,
        sortable: true,
      },
      {
        name: "Retryable",
        cell: (r) =>
          r.idempotent ? (
            <Badge bg="success">Yes</Badge>
          ) : (
            <Badge bg="warning" text="dark">
              Requires force
            </Badge>
          ),
      },
    ],
    [],
  );

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader
          title="Retry center"
          breadcrumbs={[
            { label: "Platform", path: platformUrl("dashboard") },
            { label: "Jobs", path: platformUrl("jobs") },
            { label: "Retry" },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load"
          description={error.message}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Retry center"
        breadcrumbs={[
          { label: "Platform", path: platformUrl("dashboard") },
          { label: "Jobs", path: platformUrl("jobs") },
          { label: "Retry" },
        ]}
        secondaryActions={[
          {
            label: "Back to queue",
            variant: "outline-secondary",
            onClick: () => navigate(platformUrl("jobs")),
          },
        ]}
      />

      <div className={styles.stickyFilters}>
        <Row className="g-2 align-items-end">
          <Col md={6} lg={2}>
            <Form.Label className="small text-muted">Queue</Form.Label>
            <Form.Select
              value={fQueue}
              onChange={(e) => setFQueue(e.target.value)}
            >
              <option value="">All</option>
              {[
                "attendee-sync",
                "meeting-sync",
                "transcript",
                "notifications",
              ].map((q) => (
                <option key={q} value={q}>
                  {q}
                </option>
              ))}
            </Form.Select>
          </Col>
          <Col md={6} lg={2}>
            <Form.Label className="small text-muted">Provider</Form.Label>
            <Form.Select
              value={fProvider}
              onChange={(e) => setFProvider(e.target.value)}
            >
              <option value="">All</option>
              <option value="microsoft">Microsoft</option>
              <option value="google">Google</option>
              <option value="whatsapp">WhatsApp</option>
            </Form.Select>
          </Col>
          <Col md={6} lg={2}>
            <Form.Label className="small text-muted">Status</Form.Label>
            <Form.Select
              value={fStatus}
              onChange={(e) => setFStatus(e.target.value)}
            >
              <option value="">All</option>
              <option value="failed">Failed</option>
              <option value="scheduled">Scheduled</option>
            </Form.Select>
          </Col>
          <Col md={6} lg={2}>
            <Form.Label className="small text-muted">Time range</Form.Label>
            <Form.Select
              value={fRange}
              onChange={(e) => setFRange(e.target.value)}
            >
              <option value="24h">Last 24h</option>
              <option value="7d">Last 7d</option>
            </Form.Select>
          </Col>
          <Col md={6} lg={2}>
            <Button className="mt-3" size="sm" onClick={() => load()}>
              Apply filters
            </Button>
          </Col>
        </Row>
      </div>

      <div className={styles.layout}>
        <div className={styles.main}>
          {loading && !rows.length ? (
            <LoadingSkeleton rows={6} cols={4} />
          ) : (
            <DataTableWrapper
              columns={columns}
              data={rows}
              loading={false}
              keyField="id"
              showSearch={false}
              selectableRows
              onSelectedRowsChange={({ selectedRows: sr }) => {
                const m = {};
                (sr || []).forEach((r) => {
                  m[r.id] = true;
                });
                setSelected(m);
              }}
            />
          )}
        </div>
        <aside className={`${styles.sidePanel} d-none d-lg-block`}>
          <h6 className="text-muted small">Retry options</h6>
          <Form.Label className="small">Strategy</Form.Label>
          <Form.Select
            size="sm"
            className="mb-2"
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
          >
            <option value="immediate">Immediate</option>
            <option value="delayed">Delayed</option>
            <option value="scheduled">Scheduled</option>
          </Form.Select>
          {strategy === "delayed" && (
            <>
              <Form.Label className="small">Delay (minutes)</Form.Label>
              <Form.Control
                size="sm"
                type="number"
                className="mb-2"
                value={delayMin}
                onChange={(e) => setDelayMin(+e.target.value)}
              />
            </>
          )}
          <Form.Label className="small">Backoff</Form.Label>
          <Form.Select
            size="sm"
            className="mb-2"
            value={backoff}
            onChange={(e) => setBackoff(e.target.value)}
          >
            <option value="none">None</option>
            <option value="exponential">Exponential</option>
          </Form.Select>
          <Form.Label className="small">Max retries</Form.Label>
          <Form.Control
            size="sm"
            type="number"
            className="mb-2"
            value={maxRetries}
            onChange={(e) => setMaxRetries(+e.target.value)}
          />
          <div className="small text-muted border rounded p-2 mt-2">
            <div className="fw-semibold mb-1">Estimated impact</div>~
            {selectedIds.length ? safeCount : "—"} of{" "}
            {selectedIds.length || "—"} selected jobs are idempotent and safe to
            retry.
          </div>
        </aside>
      </div>

      {selectedIds.length > 0 && (
        <div className={styles.bottomBar}>
          <div className="d-flex flex-wrap align-items-center gap-2 justify-content-between">
            <span className="fw-semibold">
              {selectedIds.length} jobs selected
            </span>
            <div className="d-flex flex-wrap gap-2">
              <Button size="sm" onClick={() => setBulkOpen(true)}>
                Retry selected
              </Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => setScheduleOpen(true)}
              >
                Schedule retry
              </Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => toast.success("Export started (simulated)")}
              >
                Export selected
              </Button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={bulkOpen}
        onClose={() => setBulkOpen(false)}
        onConfirm={async () => {
          try {
            await bulkRetryJobsAPI(selectedIds, {
              strategy,
              delayMin,
              backoff,
              maxRetries,
            });
            toast.success("Bulk retry queued");
            setBulkOpen(false);
            setSelected({});
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Retry selected jobs?"
        message={`${selectedIds.length} job(s) will be retried with the current retry options.`}
        confirmLabel="Retry"
      />

      <Modal show={scheduleOpen} onHide={() => setScheduleOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Schedule retry</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>When</Form.Label>
          <DatePicker
            selected={scheduleAt}
            onChange={(d) => d && setScheduleAt(d)}
            showTimeSelect
            dateFormat="Pp"
            className="form-control"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await scheduleRetryAPI({
                  ids: selectedIds,
                  at: scheduleAt.toISOString(),
                  strategy,
                  backoff,
                  maxRetries,
                });
                toast.success("Scheduled");
                setScheduleOpen(false);
                load();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Schedule
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
